import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { useParams } from 'react-router-dom';
import {
  Breadcrumbs,
  Flex,
  H1,
  P,
  Popover,
  Tab,
  Tag,
} from '@decisiv/ui-components';
import iconix from '@decisiv/iconix/lib/components';
import isEmpty from 'lodash/isEmpty';

import SharedSettingsApi from '../../api/shared_settings';
import { isUUID } from '../../utils/identifierUtils';
import IntegrationsApi from '../../api/integrations';

import WrapperWithLoading from '../../components/WrapperWithLoading';
import NotFound from '../../components/NotFound';
import CopyUrlButton from '../../components/CopyUrlButton';

import IntegrationDetails from './IntegrationDetails';
import IntegrationSettings from './IntegrationSettings';
import CommandsGraph from './CommandsGraph';
import CommandsLegend from './CommandsLegend';
import ExecutionsList from './ExecutionsList';
import ManualInvocation from './ManualInvocation';
import ChangeHistoryContainer from '../../containers/ChangeHistoryContainer';
import {
  CircuitBreakerAlertText,
  CommandsContainer,
  EmptyStateContainer,
  EmptyStateText,
  IntegrationDetailSection,
  IntegrationDetailTabs,
} from './style';
import { encryptStorage } from '../../utils/storage';
import authorizeOn from '../../utils/resourceAuthorizer';
import Helmet from '../../components/SEO/Helmet';
import { formatTimestamp, isAfterNow, secondsFrom } from '../../utils/dates';
import usePopoverToggle from '../../hooks/usePopoverToggle';

// KDS Tabs reads `defaultSelectedId` once, at its own first mount, and that
// mount happens on the render where `loading` flips to false. The page used to
// compute a tab right after that flip, but under React 17 the two landed in
// separate commits, so the computed value always arrived too late and Locations
// stayed active. React 18 batches them, which would have made a dormant branch
// suddenly change the landing tab. Locations is the tab this page has always
// opened on, so it is stated here instead of re-derived.
const INITIAL_TAB = 'integration-locations-tab';

export default function IntegrationDetailPage() {
  const [integration, setIntegration] = useState();
  const [integrationStepFunctions, setIntegrationStepFunctions] = useState();
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [integrationNotFound, setIntegrationNotFound] = useState(false);
  const [integrationEnabledStatus, setIntegrationEnabledStatus] =
    useState(false);
  const [integrationPublishStatus, setIntegrationPublishStatus] =
    useState(false);
  const { identifier } = useParams();
  const [displayCircuitBreaker, setDisplayCircuitBreaker] = useState(false);
  const userSession = useMemo(() => encryptStorage.getItem('userSession'), []);

  const [allSharedSettings, setAllSharedSettings] = useState([]);

  const linkedPopover = usePopoverToggle();
  const actionRefLinkedRef = useRef();

  const updateIntegrationStatus = (value, integrationField) => {
    const currentIntegration = integration;
    currentIntegration.attributes[integrationField] = value;
    setIntegrationEnabledStatus(currentIntegration.attributes.enabled);
    setIntegrationPublishStatus(currentIntegration.attributes.published);
    setIntegration(currentIntegration);
  };

  const handleIntegrationUpdated = (updatedIntegration) => {
    setIntegration(updatedIntegration);
  };

  useEffect(() => {
    // The component is reused across :identifier changes, so a request for the
    // integration just navigated away from can still land. Everything below the
    // await is guarded, or the slower response wins the page.
    let cancelled = false;

    const getData = async () => {
      setLoading(true);
      // The component survives an :identifier change, so a flag left over from
      // the previous integration would keep rendering its state under the new
      // one. Every route-scoped flag is recomputed below.
      setIntegrationNotFound(false);
      setDisplayCircuitBreaker(false);

      const fetchSingleResponse = isUUID(identifier)
        ? await IntegrationsApi.fetchIntegrationByUuid(identifier)
        : await IntegrationsApi.fetchIntegrationBySlug(identifier);

      if (cancelled) {
        return;
      }

      if (
        fetchSingleResponse.errors?.[0]?.status === 404 ||
        !fetchSingleResponse.data
      ) {
        setIntegrationNotFound(true);
        setLoading(false);
        return;
      }

      // Handle different response structures
      const integrationData =
        fetchSingleResponse.data[0] || fetchSingleResponse.data;
      const includedData = fetchSingleResponse.included || [];

      if (!integrationData || isEmpty(integrationData)) {
        setIntegrationNotFound(true);
        setLoading(false);
        return;
      }

      setIntegration(integrationData);
      setIntegrationStepFunctions(includedData);
      setLoading(false);

      setIntegrationEnabledStatus(integrationData.attributes.enabled);
      setIntegrationPublishStatus(integrationData.attributes.published);

      const breakerIsOpen =
        integrationData.attributes.circuit_breaker_enabled === true &&
        Boolean(integrationData.attributes.circuit_breaker_opened_at) &&
        isAfterNow(
          secondsFrom(
            integrationData.attributes.circuit_breaker_opened_at,
            integrationData.attributes.circuit_breaker_retry_after,
          ),
        );

      setDisplayCircuitBreaker(breakerIsOpen);
    };

    getData();

    return () => {
      cancelled = true;
    };
  }, [identifier, refresh]);

  useEffect(() => {
    async function fetchSettings() {
      const { data } = await SharedSettingsApi.fetchAllSharedSettings();
      if (data) {
        setAllSharedSettings(
          data.map((item) => ({ id: item.id, ...item.attributes })),
        );
      }
    }
    fetchSettings();
  }, []);

  const headerLinkedIntegrations = (() => {
    if (!integration?.id) return [];
    const seen = {};
    allSharedSettings
      .filter((s) => (s.integration_uuids || []).includes(integration.id))
      .forEach((s) => {
        (s.integrations || [])
          .filter((i) => i.uuid !== integration.id)
          .forEach((i) => {
            seen[i.uuid] = i;
          });
      });
    return Object.values(seen);
  })();

  // Membership on its own, not derived from headerLinkedIntegrations: a group
  // whose other members were archived still syncs this integration's locations.
  const inSharedSetting =
    !!integration?.id &&
    allSharedSettings.some((s) =>
      (s.integration_uuids || []).includes(integration.id),
    );

  return (
    <>
      <Flex padding={1}>
        {integration && (
          <>
            <Breadcrumbs
              path={[{ to: '/integrations', text: 'Integrations' }]}
              current={integration.attributes.name}
            />
            <Helmet title={integration.attributes.name} />
          </>
        )}
      </Flex>
      {integrationNotFound ? (
        <NotFound
          title={t`Integration Not Found`}
          description={t`The integration may have been archived or the URL is incorrect.`}
        />
      ) : (
        <IntegrationDetailSection
          flexDirection="column"
          justifyContent="center"
          padding={2}
          flex="none"
          flexGrow="0"
        >
          <WrapperWithLoading loading={loading}>
            <Flex>
              {integration && (
                <Flex alignItems="center">
                  <H1>{integration.attributes.name}</H1>
                  <Flex marginLeft={1}>
                    <CopyUrlButton
                      basePath="/integrations"
                      id={integration.id}
                      slug={integration.attributes.slug}
                    />
                  </Flex>
                </Flex>
              )}
              <Flex padding={1}>
                {integration && (
                  <>
                    <Tag
                      text={integrationEnabledStatus ? 'Active' : 'Inactive'}
                      icon={iconix.CircleF}
                      color={
                        integrationEnabledStatus ? 'success' : 'licoriceMousse'
                      }
                      variant="outline"
                    />
                    <Tag
                      style={{
                        marginLeft: '0.6rem',
                      }}
                      text={
                        integrationPublishStatus ? 'Published' : 'Unpublished'
                      }
                      icon={
                        integrationPublishStatus
                          ? iconix.EyeAlt
                          : iconix.EyeSlash
                      }
                      color={
                        integrationPublishStatus ? 'success' : 'licoriceMousse'
                      }
                      variant="outline"
                    />
                    {displayCircuitBreaker && (
                      <Flex>
                        <Tag
                          style={{
                            marginLeft: '0.6rem',
                          }}
                          text="Circuit Breaker"
                          icon={iconix.Circuit}
                          color="danger"
                        />
                        <CircuitBreakerAlertText>
                          <Trans>Circuit Breaker activated on: </Trans>
                          {formatTimestamp(
                            integration.attributes.circuit_breaker_opened_at,
                          )}
                        </CircuitBreakerAlertText>
                      </Flex>
                    )}
                    {headerLinkedIntegrations.length > 0 && (
                      <Flex>
                        <Tag
                          action={linkedPopover.toggle}
                          actionAttributes={{
                            'aria-label': t`Show linked integrations`,
                          }}
                          actionRef={actionRefLinkedRef}
                          actionIcon={iconix.ChevronDown}
                          icon={iconix.Link}
                          color="information"
                          text={t`Shared Settings`}
                          variant="outline"
                          style={{ marginLeft: '0.6rem' }}
                        />
                        <Popover
                          ref={linkedPopover.popoverRef}
                          target={actionRefLinkedRef}
                          manageEvents="click"
                        >
                          <Flex padding={1} flexDirection="column">
                            {headerLinkedIntegrations.map((integ) => (
                              <P key={integ.uuid} color="alaskanHusky">
                                {integ.name}
                              </P>
                            ))}
                          </Flex>
                        </Popover>
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
          </WrapperWithLoading>
        </IntegrationDetailSection>
      )}

      {!integrationNotFound && (
        <WrapperWithLoading loading={loading}>
          <IntegrationDetailTabs
            renderHiddenTabsContent={false}
            size="large"
            defaultSelectedId={INITIAL_TAB}
          >
            <Tab
              title="Locations"
              name="Locations"
              id="integration-locations-tab"
              disabled={false}
            >
              <IntegrationDetails
                integration={integration}
                setRefresh={setRefresh}
                inSharedSetting={inSharedSetting}
              />
            </Tab>
            <Tab
              title="Global Settings"
              name="Global Settings"
              id="integration-settings-tab"
              disabled={!userSession['is_global_admin?']}
            >
              {integration && (
                <IntegrationSettings
                  integration={integration}
                  updateIntegrationStatus={updateIntegrationStatus}
                  onIntegrationUpdated={handleIntegrationUpdated}
                />
              )}
            </Tab>

            <Tab
              title="Commands"
              name="Commands"
              id="integration-commands-tab"
              disabled={false}
            >
              {integrationStepFunctions?.filter(
                (sf) => sf?.type === 'integration_step_functions',
              ).length > 0 ? (
                <CommandsContainer>
                  <CommandsLegend />
                  {integrationStepFunctions.map(
                    (stepFunction) =>
                      stepFunction?.type === 'integration_step_functions' && (
                        <CommandsGraph
                          key={stepFunction.id}
                          name={integration.attributes.name}
                          startAt={
                            stepFunction.attributes?.step_function_definition
                              ?.StartAt
                          }
                          states={
                            stepFunction.attributes?.step_function_definition
                              ?.States
                          }
                        />
                      ),
                  )}
                </CommandsContainer>
              ) : (
                <EmptyStateContainer>
                  <EmptyStateText>
                    <Trans>No commands configured for this integration.</Trans>
                  </EmptyStateText>
                </EmptyStateContainer>
              )}
            </Tab>

            <Tab
              title="Executions"
              name="executions"
              id="integration-executions-tab"
              disabled={false}
            >
              {integration && <ExecutionsList integration={integration} />}
            </Tab>

            {authorizeOn() && (
              <Tab
                title="Manual Execution"
                name="manual-invocations"
                id="manual-invocations-tab"
                disabled={false}
              >
                {integration && <ManualInvocation integration={integration} />}
              </Tab>
            )}

            {authorizeOn() && (
              <Tab
                title="Change History"
                name="Change History"
                id="integration-history-tab"
                disabled={false}
              >
                {integration && (
                  <ChangeHistoryContainer integration={integration} />
                )}
              </Tab>
            )}
          </IntegrationDetailTabs>
        </WrapperWithLoading>
      )}
    </>
  );
}
